import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/", match: "/" },
  { label: "电影", to: "/category?type=电影", match: "电影" },
  { label: "电视剧", to: "/category?type=电视剧", match: "电视剧" },
  { label: "综艺", to: "/category?type=综艺", match: "综艺" },
  { label: "资讯", to: "/news", match: "news" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const solid = scrolled || !isHome;

  const isActive = (match: string) => {
    if (match === "/") return location.pathname === "/";
    if (match === "news") return location.pathname.startsWith("/news");
    if (location.pathname !== "/category") return false;
    return new URLSearchParams(location.search).get("type") === match;
  };

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          solid
            ? "bg-background-50/95 backdrop-blur border-b border-background-200"
            : "bg-gradient-to-b from-foreground-950/60 to-transparent"
        }`}
      >
        <div className={`h-16 md:h-[72px] px-4 md:px-6 flex items-center gap-4 md:gap-8 ${solid ? "" : ""}`}>
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span
              className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                solid ? "bg-primary-500 text-background-50" : "bg-background-50/20 text-background-50"
              }`}
            >
              <i className="ri-film-line text-lg"></i>
            </span>
            <span
              className={`font-heading text-lg md:text-xl font-bold tracking-tight ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              水母影视
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2 flex-1">
            {NAV_LINKS.map((link) => {
              const active = isActive(link.match);
              return (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? solid
                        ? "text-accent-600 bg-accent-50"
                        : "text-background-50 bg-background-50/20"
                      : solid
                      ? "text-foreground-700 hover:text-accent-600 hover:bg-background-100"
                      : "text-background-50/90 hover:text-background-50 hover:bg-background-50/15"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 ml-auto">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(keyword);
              }}
              className={`hidden lg:flex items-center gap-2 h-10 px-3 rounded-full border transition-colors w-56 xl:w-72 ${
                solid
                  ? "border-background-300 bg-background-100 focus-within:border-primary-400"
                  : "border-background-50/30 bg-background-50/15 focus-within:border-background-50/60"
              }`}
            >
              <i
                className={`ri-search-line text-sm ${solid ? "text-foreground-500" : "text-background-50/80"}`}
              ></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="keyword"
                placeholder="搜索电影、电视剧、综艺"
                className={`flex-1 bg-transparent text-sm outline-none ${
                  solid
                    ? "text-foreground-900 placeholder:text-foreground-400"
                    : "text-background-50 placeholder:text-background-50/60"
                }`}
              />
            </form>

            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen(true)}
              className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
                solid
                  ? "text-foreground-800 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            <Link
              to="/about"
              className={`hidden md:inline-flex items-center px-4 h-10 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname.startsWith("/about")
                  ? "bg-accent-500 text-background-50"
                  : solid
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-50 text-primary-700 hover:bg-background-100"
              }`}
            >
              关于我们
            </Link>

            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
                solid
                  ? "text-foreground-800 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
            <nav className="px-4 py-3 flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                    isActive(link.match)
                      ? "text-accent-600 bg-accent-50"
                      : "text-foreground-700"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/about"
                className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700 cursor-pointer"
              >
                关于我们
              </Link>
            </nav>
          </div>
        ) : null}
      </header>

      {searchOpen ? (
        <div className="lg:hidden fixed inset-0 z-[60] bg-background-50 flex flex-col">
          <div className="h-16 px-4 flex items-center gap-3 border-b border-background-200">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(keyword);
              }}
              className="flex-1 flex items-center gap-2 h-11 px-4 rounded-full border border-background-300 bg-background-100"
            >
              <i className="ri-search-line text-foreground-500"></i>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="keyword"
                placeholder="搜索电影、电视剧、综艺"
                className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
              />
            </form>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="text-sm text-foreground-600 px-2 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
          </div>
          <div className="p-4">
            <p className="text-xs text-foreground-500 mb-3">热门搜索</p>
            <div className="flex flex-wrap gap-2">
              {["长安十二时", "星际远征", "迷雾侦探", "味觉之旅", "未来纪元"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => submitSearch(tag)}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-700 cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}